// Import necessary libraries, components, and utilities
import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import { PromptInputBoxProps } from '@/types/components/converse/messages';
import { AlchemystStoreType } from '@/types/appStates';
import {
  convertSpeechToText,
  isSpeechRecognitionSupported,
} from '@/utils/ai/speech/s2t';
import MicrophoneIcon from '@mui/icons-material/MicRounded';
import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
import LanguageIcon from '@mui/icons-material/Language';
import BusinessIcon from '@mui/icons-material/Business';
import PsychologyIcon from '@mui/icons-material/Psychology';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { useEffect, useState } from 'react';
import { tunerOptions } from '@/constants/tuner_options';
import { Stack } from '@mui/material';

/**
 * PromptInputBox component
 *
 * Displays the chat input box with a text field for typing messages.
 * The component also includes buttons for sending messages and enabling speech recognition.
 *
 * @param value - Current value in the input box
 * @param setValue - Callback to update the value in the input box
 * @param onSend - Callback to send the message
 * @param thinking - Boolean to indicate if the assistant is "thinking"
 * @param setThinking - Callback to update the thinking state
 */

function PromptInputBox({
  value,
  setValue,
  onSend,
  thinking,
}: PromptInputBoxProps): React.ReactNode {
  // Access the Alchemyst AI store to get alchemyst and theme data
  const alchemyst = useAlchemystStoreForAi((store) => store.alchemyst);
  const [speechRecognitionEnabled, setSpeechRecognitionEnabled] =
    useState<boolean>(false); // Tracks if speech recognition is enabled
  const currentTheme = useAlchemystStoreForAi((store) => store.theme); // Access current theme
  const chat = useAlchemystStoreForAi((store) => store.chat);
  const setStoreState = useAlchemystStoreForAi((store) => store.setStoreState);
  const isGeneratingResponse = useAlchemystStoreForAi(
    (store) => store.chat?.isGeneratingResponse || false
  );

  // Updates the value in the input box when the user types
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  // Handles key presses; sends the message on "Enter" key press without shift
  const handleKeyDown = async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (thinking) return;
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault(); // Prevents a new line when Enter is pressed
      setStoreState({
        chat: {
          ...chat,
          thinking: true,
          isGeneratingResponse: true,
        },
      });
      // setThinking(true); // Show the assistant is "thinking"
      if (value.trim()) await onSend(); // Send message if there's input text
      setStoreState({
        chat: {
          ...chat,
          thinking: false,
          isGeneratingResponse: false,
        },
      });
      // setThinking(false);
    }
  };

  console.log('Thinking: ', thinking);
  console.log('Is Generating Response: ', isGeneratingResponse);

  // Function to start speech recognition and set the recognized text to input box
  const handleSpeechRecognition = async () => {
    try {
      const transcript = await convertSpeechToText(); // Convert speech to text
      setValue(transcript); // Set the recognized text in the input box
    } catch (error) {
      console.error('Error during speech recognition:', error); // Log any errors
    }
  };

  // Check if the browser supports speech recognition on component mount
  useEffect(() => {
    if (window) {
      setSpeechRecognitionEnabled(isSpeechRecognitionSupported());
    }
  }, []);

  const handleTunerToggle = (
    keyword: keyof AlchemystStoreType['chat']['tools']
  ) => {
    setStoreState({
      chat: {
        ...chat,
        tools: {
          ...chat.tools,
          [keyword]: !chat.tools[keyword],
        },
      },
    });
  };

  // Render the chat input box and action buttons (microphone, send)
  return (
    <Box>
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        px={3}
        py={2}
        gap={2}
        sx={(theme) => ({
          backgroundColor: theme.palette.background.paper,
          borderRadius: 5,
        })}
        className={
          currentTheme === 'dark' ? 'custom-border' : 'custom-border_for_light'
        }
      >
        {/* Text input and action buttons */}
        <Box display="flex" alignItems="center" gap={1}>
          <TextField
            value={value}
            onChange={handleInputChange}
            placeholder={`Ask ${alchemyst.name}`}
            onKeyDown={handleKeyDown}
            multiline
            maxRows={10}
            variant="outlined"
            InputProps={{
              sx: {
                backgroundColor: 'transparent',
                padding: 1,
                borderRadius: '5%',
                fontSize: '1.1rem',
              },
            }}
            sx={{
              flexGrow: 1,
              mr: 1,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'transparent',
                },
                '&:hover fieldset': {
                  borderColor: 'transparent',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'transparent',
                },
              },
            }}
          />

          <Tooltip
            title={!speechRecognitionEnabled ? 'Browser not supported' : ''}
          >
            <span>
              <Button
                onClick={handleSpeechRecognition}
                variant="contained"
                color="primary"
                disabled={!speechRecognitionEnabled || thinking}
                aria-label="Speak to Alchemyst"
                sx={{
                  backgroundColor: '#EAEAEA',
                  color: 'black',
                  borderRadius: '50%',
                  minWidth: '40px',
                  minHeight: '40px',
                  padding: '8px',
                  boxShadow: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: '#EAEAEA',
                  },
                }}
              >
                <MicrophoneIcon />
              </Button>
            </span>
          </Tooltip>

          <Button
            onClick={async () => {
              setStoreState({
                chat: {
                  ...chat,
                  thinking: true,
                  isGeneratingResponse: true,
                },
              });
              await onSend();
              setStoreState({
                chat: {
                  ...chat,
                  thinking: false,
                  isGeneratingResponse: false,
                },
              });
            }}
            variant="contained"
            color="primary"
            disabled={!value.trim() || thinking}
            aria-label="Send message"
            sx={{
              backgroundColor: '#EAEAEA',
              color: 'black',
              borderRadius: '50%',
              minWidth: '40px',
              minHeight: '40px',
              padding: '8px',
              boxShadow: 3,
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: '#EAEAEA',
              },
            }}
          >
            {thinking || isGeneratingResponse ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <SendIcon />
            )}
          </Button>
        </Box>

        {/* Tuner options row */}
        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          {tunerOptions.map((tuner) => {
            // Select icon based on tuner type
            const TunerIcon = (() => {
              switch (tuner.keyword) {
                case 'researchIcps':
                  return SearchIcon;
                case 'webSearch':
                  return LanguageIcon;
                case 'organizationSearch':
                  return BusinessIcon;
                case 'deepResearch':
                  return PsychologyIcon;
                default:
                  return SearchIcon;
              }
            })();

            return (
              <Button
                key={tuner.keyword}
                variant={chat.tools[tuner.keyword] ? 'contained' : 'outlined'}
                size="small"
                onClick={() => handleTunerToggle(tuner.keyword)}
                startIcon={<TunerIcon />}
                sx={{
                  textTransform: 'none',
                  borderRadius: '20px',
                  backgroundColor: chat.tools[tuner.keyword]
                    ? '#EAEAEA'
                    : 'transparent',
                  color: chat.tools[tuner.keyword] ? 'black' : '#666666',
                  border: '1px solid #EAEAEA',
                  '&:hover': {
                    backgroundColor: chat.tools[tuner.keyword]
                      ? '#D1D1D1'
                      : '#EAEAEA',
                  },

                  minWidth: 'fit-content',
                  px: 1,
                }}
              >
                {tuner.name}
              </Button>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
}

export default PromptInputBox;
